import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Pencil, Tag, Languages, Sliders, Power, Eye, EyeOff, Upload, Image as ImageIcon, Globe, Loader2, X, Check } from "lucide-react";
import MenuQRCode from "../components/MenuQRCode";
import GeneralMenuQRCode from "../components/GeneralMenuQRCode";

const LANGS = [["en", "English"], ["fr", "Français"], ["es", "Español"], ["de", "Deutsch"]];
const imgSrc = (u) => (!u ? "" : (u.startsWith("http") || u.startsWith("data:") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));
const empty = { name: "", name_en: "", description: "", description_en: "", price: "", category_id: "", image_url: "", ingredients_text: "", translations: {}, allergens: "", available: true, visible: true, recipe_id: "", station: "", tags: "", pairing_tags: "", recommendation_enabled: true, modifier_group_ids: [] };

export default function MenuManager() {
  const [cats, setCats] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [transLoading, setTransLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggestedAllergens, setSuggestedAllergens] = useState([]);
  const [showUrl, setShowUrl] = useState(false);
  const [modGroups, setModGroups] = useState([]);
  const [modOpen, setModOpen] = useState(false);
  const fileRef = useRef(null);
  // Batch translate
  const [batchOpen, setBatchOpen] = useState(false);
  const [bLangs, setBLangs] = useState({ en: true, fr: true, es: true, de: true });
  const [bOverwrite, setBOverwrite] = useState(false);
  const [bRun, setBRun] = useState(false);
  const [bProg, setBProg] = useState({ total: 0, done: 0, errors: [] });

  const load = async () => {
    const [c, d] = await Promise.all([api.get("/categories"), api.get("/dishes")]);
    setCats(c.data); setDishes(d.data);
    api.get("/food-costs").then(({ data }) => setRecipes(data)).catch(() => setRecipes([]));
    api.get("/pos/modifier-groups").then(({ data }) => setModGroups(data)).catch(() => setModGroups([]));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, category_id: cats[0]?.id || "" }); setEditId(null); setSuggestedAllergens([]); setShowUrl(false); setOpen(true); };
  const openEdit = (d) => {
    setForm({
      ...empty, ...d, price: String(d.price), allergens: (d.allergens || []).join(", "),
      ingredients_text: d.ingredients_text || "", translations: d.translations || {},
      recipe_id: d.recipe_id || "", station: d.station || "",
      tags: (d.tags || []).join(", "), pairing_tags: (d.pairing_tags || []).join(", "),
      recommendation_enabled: d.recommendation_enabled !== false,
      visible: d.visible !== false,
      modifier_group_ids: d.modifier_group_ids || [],
    });
    setEditId(d.id); setSuggestedAllergens([]); setShowUrl(!!d.image_url && d.image_url.startsWith("http")); setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price || !form.category_id) return toast.error("Compila nome, prezzo e categoria");
    const splitCsv = (s) => (s ? String(s).split(",").map((a) => a.trim()).filter(Boolean) : []);
    const payload = {
      ...form, price: parseFloat(form.price),
      allergens: splitCsv(form.allergens), tags: splitCsv(form.tags), pairing_tags: splitCsv(form.pairing_tags),
      recipe_id: form.recipe_id === "none" ? "" : (form.recipe_id || ""),
      station: form.station === "none" ? "" : (form.station || ""),
      recommendation_enabled: form.recommendation_enabled !== false,
    };
    try {
      if (editId) await api.put(`/dishes/${editId}`, payload);
      else await api.post("/dishes", payload);
      toast.success("Piatto salvato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/dishes/${id}`); toast.success("Piatto eliminato"); load(); };
  const toggleVisible = async (d) => {
    const next = d.visible === false;
    try { await api.put(`/dishes/${d.id}/visible`, { visible: next }); toast.success(next ? "Prodotto mostrato nel menù" : "Prodotto nascosto dal menù"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addCat = async () => {
    if (!newCat) return;
    await api.post("/categories", { name: newCat, order: cats.length + 1 });
    setNewCat(""); toast.success("Categoria aggiunta"); load();
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Usa JPG, PNG o WEBP");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/dish-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Foto caricata");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const genAI = async () => {
    if (!form.name) return toast.error("Inserisci prima il nome del piatto");
    setAiLoading(true);
    try {
      const catName = cats.find((c) => c.id === form.category_id)?.name || "";
      const { data } = await api.post("/ai/menu-description", { name: form.name, category: catName, ingredients_text: form.ingredients_text });
      setForm((f) => ({ ...f, description: data.description_it }));
      setSuggestedAllergens(data.suggested_allergens || []);
      toast.success("Descrizione generata con l'AI");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const confirmAllergen = (a) => {
    const cur = form.allergens ? form.allergens.split(",").map((s) => s.trim()).filter(Boolean) : [];
    if (!cur.map((x) => x.toLowerCase()).includes(a.toLowerCase())) cur.push(a);
    setForm((f) => ({ ...f, allergens: cur.join(", ") }));
    setSuggestedAllergens((s) => s.filter((x) => x !== a));
  };

  const setTr = (lang, field, val) => setForm((f) => ({ ...f, translations: { ...(f.translations || {}), [lang]: { ...((f.translations || {})[lang] || {}), [field]: val } } }));

  const genTranslations = async () => {
    if (!form.name) return toast.error("Inserisci il nome del piatto");
    const tr = form.translations || {};
    const have = LANGS.map(([l]) => l).filter((l) => tr[l]?.name);
    let overwrite = false;
    if (have.length) overwrite = window.confirm("Esistono già traduzioni.\nOK = Rigenera tutte · Annulla = completa solo le mancanti");
    const targets = overwrite ? LANGS.map(([l]) => l) : LANGS.map(([l]) => l).filter((l) => !tr[l]?.name);
    if (!targets.length) return toast.info("Traduzioni già complete");
    setTransLoading(true);
    try {
      const { data } = await api.post("/ai/menu-translations", { name_it: form.name, description_it: form.description, ingredients_text: form.ingredients_text, target_languages: targets });
      setForm((f) => ({ ...f, translations: { ...(f.translations || {}), ...data.translations } }));
      toast.success("Traduzioni generate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTransLoading(false); }
  };

  const runBatch = async (onlyIds = null) => {
    const langs = Object.keys(bLangs).filter((l) => bLangs[l]);
    if (!langs.length) return toast.error("Seleziona almeno una lingua");
    const targets = onlyIds ? dishes.filter((d) => onlyIds.includes(d.id)) : dishes;
    setBRun(true); setBProg({ total: targets.length, done: 0, errors: [] });
    try { await api.post("/ai/categories/translate", { target_languages: langs, overwrite: bOverwrite }); } catch { /* best effort */ }
    const errors = [];
    for (let i = 0; i < targets.length; i++) {
      try { await api.post(`/ai/dish/${targets[i].id}/translate`, { target_languages: langs, overwrite: bOverwrite }); }
      catch { errors.push({ id: targets[i].id, name: targets[i].name }); }
      setBProg({ total: targets.length, done: i + 1, errors: [...errors] });
    }
    setBRun(false); load();
    toast.success(`Traduzioni completate · ${targets.length - errors.length}/${targets.length}`);
  };

  return (
    <div>
      <PageHeader title="Menù Digitale" subtitle="Gestisci categorie e piatti" testid="menu-header">
        <Button onClick={() => setBatchOpen(true)} variant="outline" className="rounded-full" data-testid="translate-menu-button">
          <Globe className="w-4 h-4 mr-1" /> Traduci tutto il menù
        </Button>
        <Button onClick={() => setModOpen(true)} variant="outline" className="rounded-full" data-testid="manage-modifiers-button">
          <Sliders className="w-4 h-4 mr-1" /> Modificatori
        </Button>
        <Button onClick={openNew} className="rounded-full" data-testid="add-dish-button">
          <Plus className="w-4 h-4 mr-1" /> Nuovo piatto
        </Button>
      </PageHeader>

      <Card className="p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="category-manager">
        <Tag className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium mr-2">Categorie:</span>
        {cats.map((c) => (<span key={c.id} className="text-xs bg-secondary px-3 py-1 rounded-full">{c.name}</span>))}
        <div className="flex items-center gap-2 ml-auto">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nuova categoria" className="h-8 w-40" data-testid="new-category-input" />
          <Button size="sm" variant="outline" onClick={addCat} className="rounded-full" data-testid="add-category-button">Aggiungi</Button>
        </div>
      </Card>

      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MenuQRCode />
        <GeneralMenuQRCode />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {dishes.map((d) => (
          <Card key={d.id} className={`overflow-hidden rounded-xl hover:-translate-y-0.5 transition-transform ${d.visible === false ? "opacity-60" : ""}`} data-testid={`dish-card-${d.id}`}>
            {d.image_url ? (
              <img src={imgSrc(d.image_url)} alt={d.name} loading="lazy" className="h-36 w-full object-cover" />
            ) : (
              <div className="h-36 w-full bg-secondary flex items-center justify-center text-muted-foreground text-sm">Nessuna immagine</div>
            )}
            <div className="p-4">
              <div className="flex justify-between items-start gap-2">
                <h3 className="font-medium">{d.name}</h3>
                <span className="font-dash font-semibold text-primary whitespace-nowrap">€ {d.price.toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{cats.find((c) => c.id === d.category_id)?.name}</p>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{d.description}</p>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${d.available ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground"}`}>
                    {d.available ? "Disponibile" : "Non disponibile"}
                  </span>
                  {d.visible === false && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-medium">Nascosto dal menù</span>}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => toggleVisible(d)} data-testid={`toggle-visible-${d.id}`} title={d.visible === false ? "Mostra nel menù" : "Nascondi dal menù"}>
                    {d.visible === false ? <EyeOff className="w-4 h-4 text-amber-600" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-dish-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(d.id)} data-testid={`delete-dish-${d.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[92vh] overflow-y-auto" data-testid="dish-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica piatto" : "Nuovo piatto"}</DialogTitle>
            <DialogDescription>Nome, prezzo e categoria sono obbligatori. Il resto è facoltativo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dish-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prezzo (€)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="dish-price-input" />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="dish-category-select"><SelectValue placeholder="Scegli" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* FOTO DEL PIATTO */}
            <div className="space-y-2">
              <Label>Foto del piatto</Label>
              {form.image_url ? (
                <div className="relative rounded-xl overflow-hidden border">
                  <img src={imgSrc(form.image_url)} alt="anteprima" className="w-full h-44 object-cover" data-testid="dish-image-preview" />
                  <div className="absolute bottom-2 right-2 flex gap-2">
                    <Button type="button" size="sm" variant="secondary" className="rounded-full h-8" onClick={() => fileRef.current?.click()} data-testid="dish-replace-photo"><Upload className="w-3.5 h-3.5 mr-1" /> Sostituisci</Button>
                    <Button type="button" size="sm" variant="destructive" className="rounded-full h-8" onClick={() => setForm({ ...form, image_url: "" })} data-testid="dish-remove-photo"><X className="w-3.5 h-3.5 mr-1" /> Elimina</Button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dish-upload-photo"
                  className="w-full h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary/60 transition-colors">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImageIcon className="w-6 h-6" />}
                  <span className="text-sm font-medium">{uploading ? "Caricamento…" : "CARICA FOTO"}</span>
                  <span className="text-xs">JPG, PNG o WEBP · da PC, galleria o fotocamera</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickFile} data-testid="dish-file-input" />
              <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setShowUrl((v) => !v)} data-testid="toggle-url-input">Inserisci URL manualmente (avanzato)</button>
              {showUrl && <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" data-testid="dish-image-input" />}
            </div>

            {/* INGREDIENTI */}
            <div className="space-y-2">
              <Label>Ingredienti</Label>
              <Textarea value={form.ingredients_text} onChange={(e) => setForm({ ...form, ingredients_text: e.target.value })} rows={2}
                placeholder="Es. zucca, carote, salsiccia paesana, scaglie di grana" data-testid="dish-ingredients-input" />
            </div>

            {/* DESCRIZIONE ITALIANA + AI */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Descrizione (italiano)</Label>
                <Button type="button" size="sm" variant="outline" onClick={genAI} disabled={aiLoading} className="rounded-full h-7" data-testid="ai-generate-button">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> {aiLoading ? "Genero…" : "Genera descrizione con AI"}
                </Button>
              </div>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="dish-description-input" />
              {form.description && <Button type="button" size="sm" variant="ghost" onClick={genAI} disabled={aiLoading} className="h-7 text-xs" data-testid="ai-regenerate-button"><Sparkles className="w-3 h-3 mr-1" /> Rigenera descrizione</Button>}
              {suggestedAllergens.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2" data-testid="suggested-allergens">
                  <p className="text-xs text-amber-800 mb-1">Possibili allergeni rilevati (conferma manuale):</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedAllergens.map((a) => (
                      <span key={a} className="inline-flex items-center gap-1 text-xs bg-white border rounded-full pl-2.5 pr-1 py-0.5">
                        {a}
                        <button type="button" onClick={() => confirmAllergen(a)} className="text-emerald-600 p-0.5" title="Conferma" data-testid={`allergen-confirm-${a}`}><Check className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => setSuggestedAllergens((s) => s.filter((x) => x !== a))} className="text-muted-foreground p-0.5" title="Ignora" data-testid={`allergen-ignore-${a}`}><X className="w-3.5 h-3.5" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* TRADUZIONI */}
            <div className="rounded-lg border border-dashed p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Languages className="w-3.5 h-3.5" /> Traduzioni</p>
                <Button type="button" size="sm" variant="outline" onClick={genTranslations} disabled={transLoading} className="rounded-full h-7" data-testid="gen-translations-button">
                  <Globe className="w-3.5 h-3.5 mr-1" /> {transLoading ? "Traduco…" : "Genera traduzioni"}
                </Button>
              </div>
              <Tabs defaultValue="en">
                <TabsList className="flex flex-wrap h-auto">
                  {LANGS.map(([l, name]) => <TabsTrigger key={l} value={l} data-testid={`trans-tab-${l}`}>{name}</TabsTrigger>)}
                </TabsList>
                {LANGS.map(([l]) => (
                  <TabsContent key={l} value={l} className="space-y-2 mt-2">
                    <Input value={form.translations?.[l]?.name || ""} onChange={(e) => setTr(l, "name", e.target.value)} placeholder="Nome" data-testid={`trans-name-${l}`} />
                    <Textarea value={form.translations?.[l]?.description || ""} onChange={(e) => setTr(l, "description", e.target.value)} rows={2} placeholder="Descrizione" data-testid={`trans-desc-${l}`} />
                  </TabsContent>
                ))}
              </Tabs>
            </div>

            {/* ALLERGENI */}
            <div className="space-y-2">
              <Label>Allergeni (separati da virgola)</Label>
              <Input value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} placeholder="glutine, latte" data-testid="dish-allergens-input" />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="dish-available-switch" />
              <Label>Disponibile a menù</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.visible !== false} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="dish-visible-switch" />
              <Label>Visibile nel menù pubblico (generale e tavoli)</Label>
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-lg border border-dashed p-3">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Ricetta & raccomandazioni AI (opzionale)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ricetta Food Cost</Label>
                  <Select value={form.recipe_id || "none"} onValueChange={(v) => setForm({ ...form, recipe_id: v })}>
                    <SelectTrigger data-testid="dish-recipe-select"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna</SelectItem>
                      {recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Postazione</Label>
                  <Select value={form.station || "none"} onValueChange={(v) => setForm({ ...form, station: v })}>
                    <SelectTrigger data-testid="dish-station-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="cucina">Cucina</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tag (separati da virgola)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="aperitivo, estivo, fresco" data-testid="dish-tags-input" />
              </div>
              <div className="space-y-2">
                <Label>Abbinamenti (pairing tags)</Label>
                <Input value={form.pairing_tags} onChange={(e) => setForm({ ...form, pairing_tags: e.target.value })} placeholder="spritz, tagliere, fritto" data-testid="dish-pairing-input" />
              </div>
              {modGroups.length > 0 && (
                <div className="space-y-2">
                  <Label>Gruppi modificatori (POS)</Label>
                  <div className="flex flex-wrap gap-2">
                    {modGroups.map((g) => {
                      const on = (form.modifier_group_ids || []).includes(g.id);
                      return (
                        <button type="button" key={g.id} data-testid={`dish-modgroup-${g.id}`}
                          onClick={() => setForm((f) => ({ ...f, modifier_group_ids: on ? f.modifier_group_ids.filter((x) => x !== g.id) : [...(f.modifier_group_ids || []), g.id] }))}
                          className={`text-xs px-3 py-1.5 rounded-full border ${on ? "border-primary bg-primary/10" : "border-border"}`}>
                          {g.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Switch checked={form.recommendation_enabled} onCheckedChange={(v) => setForm({ ...form, recommendation_enabled: v })} data-testid="dish-reco-switch" />
                <Label>Abilita nei suggerimenti AI</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-full" data-testid="save-dish-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BATCH TRANSLATE */}
      <Dialog open={batchOpen} onOpenChange={(o) => { if (!bRun) setBatchOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="batch-translate-dialog">
          <DialogHeader>
            <DialogTitle>Traduci tutto il menù</DialogTitle>
            <DialogDescription>Genera le traduzioni mancanti per tutti i {dishes.length} prodotti e le categorie.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {LANGS.map(([l, name]) => (
                <label key={l} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={bLangs[l]} onCheckedChange={(v) => setBLangs((s) => ({ ...s, [l]: !!v }))} data-testid={`batch-lang-${l}`} /> {name}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={bOverwrite} onCheckedChange={(v) => setBOverwrite(!!v)} data-testid="batch-overwrite" />
              Sovrascrivi traduzioni esistenti <span className="text-xs text-muted-foreground">(default: solo mancanti)</span>
            </label>
            {(bRun || bProg.total > 0) && (
              <div className="space-y-1" data-testid="batch-progress">
                <Progress value={bProg.total ? (bProg.done / bProg.total) * 100 : 0} />
                <p className="text-xs text-muted-foreground">In corso: {bProg.done} / {bProg.total} · Errori: {bProg.errors.length}</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {bProg.errors.length > 0 && !bRun && (
              <Button variant="outline" className="rounded-full" onClick={() => runBatch(bProg.errors.map((e) => e.id))} data-testid="batch-retry-errors">Riprova errori ({bProg.errors.length})</Button>
            )}
            <Button onClick={() => runBatch()} disabled={bRun} className="rounded-full" data-testid="batch-run-button">
              {bRun ? <Loader2 className="w-4 h-4 animate-spin" /> : "Genera traduzioni"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModifiersManager open={modOpen} onOpenChange={setModOpen} recipes={recipes} reload={load} />
    </div>
  );
}

function ModifiersManager({ open, onOpenChange, recipes, reload }) {
  const [groups, setGroups] = useState([]);
  const [gName, setGName] = useState("");
  const [mForm, setMForm] = useState({ group_id: "", name: "", price_delta: "", recipe_id: "" });
  const refresh = async () => { try { const { data } = await api.get("/pos/modifier-groups"); setGroups(data); } catch { /* noop */ } };
  useEffect(() => { if (open) refresh(); }, [open]);
  const addGroup = async () => { if (!gName.trim()) return; await api.post("/pos/modifier-groups", { name: gName.trim() }); setGName(""); refresh(); reload(); };
  const delGroup = async (id) => { if (!window.confirm("Eliminare il gruppo e i suoi modificatori?")) return; await api.delete(`/pos/modifier-groups/${id}`); refresh(); reload(); };
  const addMod = async (gid) => {
    if (!mForm.name.trim()) { toast.error("Nome modificatore obbligatorio"); return; }
    await api.post("/pos/modifiers", { group_id: gid, name: mForm.name.trim(), price_delta: parseFloat(mForm.price_delta) || 0, recipe_id: mForm.recipe_id === "none" ? "" : mForm.recipe_id, active: true });
    setMForm({ group_id: "", name: "", price_delta: "", recipe_id: "" }); refresh(); reload();
  };
  const toggleMod = async (m) => { await api.put(`/pos/modifiers/${m.id}`, { group_id: m.group_id, name: m.name, price_delta: m.price_delta || 0, recipe_id: m.recipe_id || "", active: !m.active }); refresh(); reload(); };
  const delMod = async (id) => { await api.delete(`/pos/modifiers/${id}`); refresh(); reload(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modifiers-manager">
        <DialogHeader><DialogTitle>Modificatori / Varianti</DialogTitle><DialogDescription className="text-xs">Crea gruppi (es. Cottura, Extra, Gin) e i relativi modificatori con prezzo e impatto ricetta opzionale. Assegna poi i gruppi ai piatti.</DialogDescription></DialogHeader>
        <div className="flex gap-2 mb-4">
          <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Nuovo gruppo (es. Extra)" data-testid="modgroup-name-input" />
          <Button onClick={addGroup} className="rounded-full" data-testid="add-modgroup-button">Aggiungi gruppo</Button>
        </div>
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.id} className="rounded-xl border border-border p-3" data-testid={`modgroup-${g.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{g.name}</span>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => delGroup(g.id)} data-testid={`del-modgroup-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-1 mb-2">
                {(g.modifiers || []).map((m) => (
                  <div key={m.id} className="flex items-center gap-2 text-sm" data-testid={`modifier-row-${m.id}`}>
                    <span className={`flex-1 ${m.active ? "" : "opacity-40 line-through"}`}>{m.name}{m.price_delta ? ` · +€${Number(m.price_delta).toFixed(2)}` : ""}{m.recipe_id ? " · ricetta" : ""}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleMod(m)} data-testid={`toggle-mod-${m.id}`}><Power className={`w-3.5 h-3.5 ${m.active ? "text-emerald-600" : "text-muted-foreground"}`} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delMod(m.id)} data-testid={`del-mod-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-end border-t border-border pt-2">
                <Input className="h-8 w-32" placeholder="Nome" value={mForm.group_id === g.id ? mForm.name : ""} onChange={(e) => setMForm({ group_id: g.id, name: e.target.value, price_delta: mForm.group_id === g.id ? mForm.price_delta : "", recipe_id: mForm.group_id === g.id ? mForm.recipe_id : "" })} data-testid={`mod-name-${g.id}`} />
                <Input className="h-8 w-24" placeholder="+€" type="number" value={mForm.group_id === g.id ? mForm.price_delta : ""} onChange={(e) => setMForm({ ...mForm, group_id: g.id, price_delta: e.target.value })} data-testid={`mod-price-${g.id}`} />
                <select className="h-8 rounded-md border border-input bg-background px-2 text-sm" value={mForm.group_id === g.id ? (mForm.recipe_id || "none") : "none"} onChange={(e) => setMForm({ ...mForm, group_id: g.id, recipe_id: e.target.value })} data-testid={`mod-recipe-${g.id}`}>
                  <option value="none">Nessuna ricetta</option>
                  {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <Button size="sm" className="rounded-full h-8" onClick={() => addMod(g.id)} data-testid={`add-mod-${g.id}`}>+ Modificatore</Button>
              </div>
            </div>
          ))}
          {groups.length === 0 && <p className="text-sm text-muted-foreground">Nessun gruppo. Creane uno per iniziare.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
